import type {
    CardBlockConfig,
    ChildBlockConfig,
    SectionBlockConfig,
} from '~~/layers/base/types/blockConfigs'

export type FieldType = 'color' | 'text' | 'motif' | 'monogram' | 'liner' | 'image'

export interface FieldsetConfig {
    id: string
    path?: string
    mobile: {
        title: string
        subtitle?: string
    }
    /**
     * Desktop-only header overrides (icon + copy).
     * If omitted, desktop falls back to `mobile`.
     */
    desktop?: {
        icon?: string
        title: string
        subtitle?: string
    }
    fieldTypes: FieldType[]
}

export type FormPartType = 'outside-of-card' | 'card' | 'envelope'

export interface FormPartConfig {
    id: FormPartType
    title: string
    subtitle: string
    fieldsets: FieldsetConfig[]
}

/** A map of the fieldsets used in different form parts. */
const fieldsets: {
    color: FieldsetConfig
    text: FieldsetConfig
    motif: FieldsetConfig
    monogram: FieldsetConfig
    liner: FieldsetConfig
    photo: FieldsetConfig
    returnAddress: FieldsetConfig
} = {
    color: {
        id: 'color',
        mobile: {
            title: 'Color',
            subtitle: 'Choose a color for all of the text and motifs',
        },
        desktop: {
            icon: 'lucide:palette',
            title: 'Choose Ink Color',
            subtitle:
                'Select a color for the writing and other elements. Add additional colors to add variety',
        },
        fieldTypes: ['color'],
    },
    text: {
        id: 'text',
        mobile: {
            title: 'Text & Style',
            subtitle: 'Write and stylize your text',
        },
        desktop: {
            icon: 'lucide:scroll-text',
            title: 'Add Inscriptions & Style',
            subtitle: 'Write your messages and style the font',
        },
        fieldTypes: ['text'],
    },
    motif: {
        id: 'motif',
        mobile: {
            title: 'Motif',
        },
        desktop: {
            icon: 'ph:anchor-simple',
            title: 'Choose Motif',
            subtitle: 'Add a flourish',
        },
        fieldTypes: ['motif'],
    },
    monogram: {
        id: 'monogram',
        mobile: {
            title: 'Monogram',
        },
        desktop: {
            icon: 'lucide:sigma',
            title: 'Create Monogram',
            subtitle: 'Design a monogram unique to you',
        },
        fieldTypes: ['monogram'],
    },
    /** Fieldset for liners, only used with envelopes */
    liner: {
        id: 'liner',
        mobile: {
            title: 'Liner',
            subtitle: 'Change the lining inside the envelope flap',
        },
        desktop: {
            icon: 'lucide:mail-open',
            title: 'Choose Envelope Liner',
            subtitle: 'Select a beautiful color or pattern to insert into the inside envelope flap',
        },
        fieldTypes: ['liner'],
    },
    /** Fieldset for any digital assets to be printed and attached/included */
    photo: {
        id: 'photo',
        mobile: {
            title: 'Photo Upload',
            subtitle: 'Add your favorite photo',
        },
        fieldTypes: ['image'],
    },
    /** Alternate text-based fieldset for envelopes, will require different fields */
    returnAddress: {
        id: 'returnAddress',
        mobile: {
            title: 'Return Address',
            subtitle:
                'Choose if you would like a return address on the envelopes. Enter and style the address.',
        },
        desktop: {
            icon: 'lucide:mailbox',
            title: 'Add Return Address',
            subtitle: 'Add a return address to the back flap of the envelope',
        },
        fieldTypes: ['text'],
    },
}

/**
 * A list of the different form part configs, each containing static text
 * to display for that form section, plus a list of the fieldset configs.
 *
 * Used to render different static text or field orders depending on
 * product parts. (e.g. Text fields for "envelope")
 */
const formPartConfigs: FormPartConfig[] = [
    // This stucture is used for all ProductParts that DO NOT have the word "Envelope" in their name
    {
        id: 'card',
        title: 'Personalize Card',
        subtitle: 'Create your perfect stationery',
        fieldsets: [
            fieldsets.photo,
            fieldsets.color,
            fieldsets.text,
            fieldsets.motif,
            fieldsets.monogram,
        ],
    },
    // This stucture is used for all ProductParts that have the word "Envelope" in their name
    {
        id: 'envelope',
        title: 'Personalize Envelope',
        subtitle: 'Enter your return address and choose a liner',
        fieldsets: [
            fieldsets.color,
            fieldsets.returnAddress,
            fieldsets.motif,
            fieldsets.monogram,
            fieldsets.liner,
        ],
    },
]

export const getAdditionalColorsOverlayConfig = (
    printingProcessName: string,
    additionalContent: ChildBlockConfig[] = []
): SectionBlockConfig & { overlayTitle: string } => ({
    id: 'additional-colors-overlay',
    overlayTitle: 'Additional Colors',
    ui: 'p-0',
    type: 'section',
    skipContainer: true,
    content: [
        {
            id: 'hero',
            type: 'hero',
            bgImage: {
                id: 'hero-bg-img',
                imageId: '1bebd55a-cd79-4978-ca10-c181333dee00',
                aspectRatio: 'landscape',
            },
            title: `What is Multi-Color ${printingProcessName}?`,
            ui: 'h-49 text-inverted mb-4',
        },
        {
            id: 'notice-card-section',
            type: 'section',
            ui: '!mb-0',
            content: [
                {
                    id: 'notice-card',
                    type: 'card',
                    variant: 'info',
                    content: [
                        {
                            id: 'addition-color',
                            type: 'text',
                            text: 'Each additional color is an added cost',
                            ui: 'font-bold mb-3',
                        },
                        // Responsive layout via AdditionalColorsPricingBlock (useIsDesktop): mobile = grid, desktop = single row with dividers
                        {
                            id: 'additional-colors-costs-row-1',
                            type: 'additional-colors-pricing',
                            items: [
                                ['Thermography', '+$100'],
                                ['Letterpress', '+$150'],
                                ['Engraving', '+$200'],
                            ],
                        },
                    ],
                },
            ],
        },
        ...additionalContent,
    ],
})

export const additionalColorsEngravingConfig: ChildBlockConfig[] = [
    {
        id: 'crafted-with-precision',
        type: 'section',
        ui: '!mb-4',
        mobileUi: 'text-center !mb-0',
        content: [
            {
                id: 'crafted-precision-row',
                type: 'row',
                mobileStack: true,
                cols: [
                    {
                        content: [
                            {
                                id: 'crafted-plates',
                                type: 'section',
                                skipContainer: true,
                                title: 'Crafted With Precision',
                                ui: 'pt-0 !mb-0 !pb-0',
                                content: [
                                    {
                                        id: 'crafted-plates-text',
                                        type: 'text',
                                        text: 'Every color requires its own plate, press, and careful alignment. Skilled artisans control pressure and ink flow to ensure each hue sits perfectly on the page, creating a result that feels both tactile and luxurious.',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        content: [
                            {
                                id: 'wide-image-img',
                                type: 'image',
                                desktopAspectRatio: '294/237',
                                image: {
                                    id: 'printing-press-golden',
                                    alt: 'Ink rollers from a printing press loaded with golden ink',
                                    imageId: 'c8d18589-e424-41ff-d6f3-23d5dd68d200',
                                    aspectRatio: 'landscape',
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'engraving-impression',
        type: 'section',
        ui: '!mb-4',
        content: [
            {
                id: 'impression-depth-row',
                type: 'row',
                desktopReverse: true,
                cols: [
                    {
                        content: [
                            {
                                id: 'depth-no-other-process',
                                type: 'section',
                                skipContainer: true,
                                title: 'A Depth No Other Process Can Match',
                                ui: 'pt-0 !mb-0 !pb-0',
                                content: [
                                    {
                                        id: 'engraved-impression-dimension',
                                        type: 'text',
                                        text: 'The engraved impression adds dimension to color, catching light and shadow in a way flat printing never can.',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        content: [
                            {
                                id: 'engraving-impression-img',
                                type: 'image',
                                desktopAspectRatio: '294/237',
                                image: {
                                    id: 'printing-press-red',
                                    alt: 'A printing press machine loaded with red ink',
                                    imageId: 'a0ea5df8-49d4-40f0-7c7d-a22ab4d13200',
                                    aspectRatio: 'portrait',
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'legacy-elevated',
        type: 'section',
        hideOnDesktop: true,
        title: 'A Legacy Elevated in Color',
        content: [
            {
                id: 'handled-with-care',
                type: 'text',
                text: 'From timeless single-color engraving to bold multi-color combinations, every design is handled with the same care Crane has practiced for generations. The result is stationery that carries tradition forward—made even more striking with color.',
            },
        ],
        ui: 'text-center !mb-4',
    },
    {
        id: 'engraving-press',
        type: 'section',
        mobileUi: 'pb-12',
        content: [
            {
                id: 'engraving-press-img',
                type: 'image',
                image: {
                    id: 'printing-press-golden-2',
                    alt: 'Ink rollers from a printing press loaded with golden ink',
                    imageId: '140e590c-ec2c-47d9-5b95-7fc5ce4a7a00',
                    aspectRatio: 'landscape',
                },
            },
        ],
    },
]

export const additionalColorsLetterpressConfig: ChildBlockConfig[] = [
    {
        id: 'impeccable-craftsmanship',
        type: 'section',
        ui: '!mb-4',
        mobileUi: 'text-center !mb-0',
        content: [
            {
                id: 'impeccable-craftsmanship-row',
                type: 'row',
                mobileStack: true,
                cols: [
                    {
                        content: [
                            {
                                id: 'crafted-plates',
                                type: 'section',
                                skipContainer: true,
                                title: 'Impeccable Craftsmanship',
                                ui: 'pt-0 !mb-0 !pb-0',
                                content: [
                                    {
                                        id: 'crafted-plates-text',
                                        type: 'text',
                                        text: 'Each color requires its own plate, and careful alignment—pressed, creating stationery that feels beautifully tactile and refined.',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        content: [
                            {
                                id: 'wide-image-img',
                                type: 'image',
                                desktopAspectRatio: '294/237',
                                image: {
                                    id: 'letterpress-card',
                                    alt: 'A person using a letterpress machine to print a letterpress card',
                                    imageId: '019b1f8f-0a0d-4a39-2533-9b6461067d00',
                                    aspectRatio: 'landscape',
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'letterpress-impression',
        type: 'section',
        ui: '!mb-4',
        content: [
            {
                id: 'impression-depth-row',
                type: 'row',
                desktopReverse: true,
                cols: [
                    {
                        content: [
                            {
                                id: 'visual-elegance',
                                type: 'section',
                                skipContainer: true,
                                title: 'Visual Elegance',
                                ui: 'pt-0 !mb-0 !pb-0',
                                content: [
                                    {
                                        id: 'visual-elegance-text',
                                        type: 'text',
                                        text: 'Now considered an art form, Letterpress leaves a deep, debossed impression, adding dimension and texture that other processes cannot achieve. Ideal for bold fonts.',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        content: [
                            {
                                id: 'engraving-impression-img',
                                type: 'image',
                                desktopAspectRatio: '294/237',
                                image: {
                                    id: 'letterpress-wedding-invitation',
                                    alt: 'A letterpress machine with a wedding invitation card',
                                    imageId: '067966f6-3aa8-417d-6ecd-cee191975900',
                                    aspectRatio: 'portrait',
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'heritage-in-every-hue',
        type: 'section',
        hideOnDesktop: true,
        title: 'Heritage in Every Hue',
        content: [
            {
                id: 'heritage-in-every-hue-text',
                type: 'text',
                text: "From classic single-color designs to bold multi-color combinations, every piece reflects Crane's generations of craftsmanship and tradition. Our Lettra paper has become the gold standard for letterpress printing across the world.",
            },
        ],
        ui: 'text-center !mb-4',
    },
    {
        id: 'engraving-press',
        type: 'section',
        mobileUi: 'pb-12',
        content: [
            {
                id: 'letterpress-machine-img',
                type: 'image',
                image: {
                    id: 'letterpress-machine',
                    alt: 'Black and white image of a person operating a letterpress machine',
                    imageId: '6f073465-f590-4abb-8e0c-669caddfb000',
                    aspectRatio: 'landscape',
                },
            },
        ],
    },
]

export const additionalColorsThermographyFlatPrintConfig: ChildBlockConfig[] = [
    {
        id: 'precision-in-every-hue',
        type: 'section',
        ui: '!mb-4',
        mobileUi: 'text-center !mb-0',
        content: [
            {
                id: 'precision-row',
                type: 'row',
                mobileStack: true,
                cols: [
                    {
                        content: [
                            {
                                id: 'precision-in-every-hue-section',
                                type: 'section',
                                skipContainer: true,
                                title: 'Precision in Every Hue',
                                ui: 'pt-0 !mb-0 !pb-0',
                                content: [
                                    {
                                        id: 'precision-in-every-hue-text',
                                        type: 'text',
                                        text: 'Thermography uses heat to raise powdered ink, creating a glossy, slightly raised finish that adds refinement and dimension to every piece. Each color is carefully applied in sequence, ensuring perfect alignment and vibrancy. The result is stationery that is visually striking and meticulously crafted. ',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        content: [
                            {
                                id: 'wide-image-img',
                                type: 'image',
                                desktopAspectRatio: '294/237',
                                image: {
                                    id: 'thermography-card',
                                    alt: 'A thermography card printed with a golden ink',
                                    imageId: '81779752-1378-4410-d594-e13fd6303000',
                                    aspectRatio: 'landscape',
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'letterpress-impression',
        type: 'section',
        ui: '!mb-4',
        content: [
            {
                id: 'impression-depth-row',
                type: 'row',
                desktopReverse: true,
                cols: [
                    {
                        content: [
                            {
                                id: 'vivid-dimension',
                                type: 'section',
                                skipContainer: true,
                                title: 'Vivid Dimension',
                                ui: 'pt-0 !mb-0 !pb-0',
                                content: [
                                    {
                                        id: 'vivid-dimension-text',
                                        type: 'text',
                                        text: 'Multi-color printing adds richness and depth that flat, single-color processes cannot achieve, giving every piece a lively, sophisticated presence.',
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        content: [
                            {
                                id: 'thermography-machine-img',
                                type: 'image',
                                desktopAspectRatio: '294/237',
                                image: {
                                    id: 'thermography-machine',
                                    alt: 'A thermography machine with other printmaking machines in the background',
                                    imageId: 'b362c378-1600-4d3b-7058-7ab8e6e8e400',
                                    aspectRatio: 'portrait',
                                },
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        id: 'craftsmanship-in-every-color',
        type: 'section',
        hideOnDesktop: true,
        title: 'Craftsmanship in Every Color',
        content: [
            {
                id: 'craftsmanship-in-every-color-text',
                type: 'text',
                text: "From timeless palettes to bold combinations, every design reflects Crane's dedication to artistry and tradition, creating stationery that celebrates both color and heritage.",
            },
        ],
        ui: 'text-center !mb-4',
    },
    {
        id: 'thermography-machine-2',
        type: 'section',
        mobileUi: 'pb-12',
        content: [
            {
                id: 'thermography-machine-2-img',
                type: 'image',
                image: {
                    id: 'thermography-machine-2',
                    alt: 'A thermography card being measured by a person',
                    imageId: '0ce6c906-ba5e-4be7-aaec-6672b1e15700',
                    aspectRatio: 'landscape',
                },
            },
        ],
    },
]

export const personalizeFormPartConfigs = formPartConfigs
export const personalizeFieldsets = fieldsets

/**
 * Returns a CardBlockConfig for displaying an incompatible color warning.
 * @param processName - The name of the printing process that is incompatible with the selected color
 * @returns CardBlockConfig with error variant and warning message
 */
export const getIncompatibleColorWarningConfig = (processName: string): CardBlockConfig => ({
    id: 'incompatible-color-warning',
    type: 'card',
    variant: 'error',
    content: [
        {
            id: 'error-text',
            type: 'text',
            text: 'Incompatible Ink Color',
            ui: 'font-bold mb-3',
        },
        {
            id: 'warning-text',
            type: 'text',
            text: `${processName} is incompatible with the selected ink color. Please choose a different ink color or printing process.`,
            ui: 'font-medium',
        },
    ],
})
